/**
 * Theme core (framework-free). The design system switches every token on a
 * `.dark` class on <html> (see design/styles/index.css); this module is the
 * only place that toggles it.
 *
 * Preference: "light" | "dark" | "system". Stored in localStorage so the
 * inline script in index.html can apply it BEFORE first paint (no flash),
 * and saved to the user's backend settings so it follows them across devices.
 */

export const THEME_PREFERENCES = ["system", "light", "dark"] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type ResolvedTheme = "light" | "dark";

/** Keep in sync with the inline script in src/index.html. */
export const THEME_STORAGE_KEY = "anthm.theme";

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && (THEME_PREFERENCES as readonly string[]).includes(value);
}

export function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function storePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage unavailable (private mode etc.): theme still applies for this session.
  }
}

export const systemPrefersDark = (): boolean =>
  globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;

export function resolveTheme(preference: ThemePreference, prefersDark = systemPrefersDark()): ResolvedTheme {
  if (preference === "system") return prefersDark ? "dark" : "light";
  return preference;
}

export function applyTheme(theme: ResolvedTheme, root: HTMLElement = document.documentElement): void {
  root.classList.toggle("dark", theme === "dark");
  // Native UI (scrollbars, form controls, autofill) follows too.
  root.style.colorScheme = theme;
}
